import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Chip,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import unifiedApi from '../../../utils/unifiedApi';

const PlayerPositionsManager = ({ canEdit = false }) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [newPositionValue, setNewPositionValue] = useState('');

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await unifiedApi.get('/api/Tenant/player-positions');
      setPositions(data || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const handleOpenDialog = (position = null) => {
    setEditingPosition(position);
    setNewPositionValue(position ? position.value : '');
    setDialogOpen(true);
    setMessage({ type: '', text: '' });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPosition(null);
    setNewPositionValue('');
  };

  const handleSavePosition = async () => {
    if (!newPositionValue.trim()) {
      setMessage({ type: 'error', text: 'Position value is required' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      if (editingPosition) {
        // Update existing position
        await unifiedApi.put('/api/Tenant/player-positions', {
          id: editingPosition.id,
          value: newPositionValue.trim(),
        });

        setMessage({ type: 'success', text: 'Position updated successfully' });
      } else {
        // Create new position
        await unifiedApi.post('/api/Tenant/player-positions', {
          value: newPositionValue.trim(),
        });

        setMessage({ type: 'success', text: 'Position created successfully' });
      }

      await fetchPositions();
      handleCloseDialog();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePosition = async (position) => {
    if (!window.confirm(`Are you sure you want to delete the position "${position.value}"?`)) {
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await unifiedApi.delete(`/api/Tenant/player-positions/${position.id}`);

      setMessage({ type: 'success', text: 'Position deleted successfully' });
      await fetchPositions();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h6'>Player Positions</Typography>
        {canEdit && (
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={saving}
        >
          Add Position
        </Button>
        )}
      </Box>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Define the player positions available in your organization. These will be available as options when adding or editing players.
      </Typography>

      <Stack direction='row' spacing={1} flexWrap='wrap' gap={1}>
        {positions.map((position) => (
          <Chip
            key={position.id}
            label={position.value}
            variant='outlined'
            onDelete={canEdit ? () => handleDeletePosition(position) : undefined}
            deleteIcon={canEdit ? <DeleteIcon /> : undefined}
            onClick={canEdit ? () => handleOpenDialog(position) : () => {}}
            clickable={canEdit}
            disabled={saving}
            icon={canEdit ? <EditIcon sx={{ fontSize: 16 }} /> : undefined}
          />
        ))}
        {positions.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No positions defined yet. Click "Add Position" to create your first position.
          </Typography>
        )}
      </Stack>

      {/* Add/Edit Position Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
        <DialogTitle>
          {editingPosition ? 'Edit Position' : 'Add New Position'}
          <IconButton
            aria-label='close'
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Position Value'
            fullWidth
            variant='outlined'
            value={newPositionValue}
            onChange={(e) => setNewPositionValue(e.target.value)}
            disabled={saving}
            helperText='Enter a short abbreviation or name for the position (e.g., GK, FW, MF, FB)'
            inputProps={{ maxLength: 20 }}
            sx={{ mt: 2 }}
          />
          {message.text && (
            <Alert severity={message.type} sx={{ mt: 2 }}>
              {message.text}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePosition}
            variant='contained'
            disabled={saving || !newPositionValue.trim()}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : editingPosition ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PlayerPositionsManager;
